import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { debounceTime } from 'rxjs';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { Tc, EnumSiNoTodos } from 'src/app/components/core/_infrastructure/types/common';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { FilterSearchingEmpresaDeshidratadoraViewModel } from '../empresadeshidratadora.types';
import { DialogMode } from 'src/app/components/_campanias/types/enum';

@Component({
    selector: 'app-empresadeshidratadora-filter',
    templateUrl: './empresadeshidratadora-filter.component.html',
    styleUrls: ['./empresadeshidratadora-filter.component.scss'],
})
export class EmpresadeshidratadoraFilterComponent extends ComponentBase implements OnInit {
    @Input() listActivos: SelectItem[];
    @Input() provincias: Tc[];
    @Input() municipios: Tc[];
    @Input() lastFilter: FilterSearchingEmpresaDeshidratadoraViewModel;
    @Input() mode: DialogMode;
    @Output() search = new EventEmitter<FilterSearchingEmpresaDeshidratadoraViewModel>();
    @Output() cleanForm = new EventEmitter<any>(); // Informa que el formulario se ha reseteado
    @Output() provinciaSelected = new EventEmitter<number>();

    listSiNoTodos: SelectItem[] = [
        { label: 'Sí', value: EnumSiNoTodos.SI },
        { label: 'NO', value: EnumSiNoTodos.NO },
        { label: 'TODOS', value: EnumSiNoTodos.TODOS },
    ];

    isCollapsed = false;
    dialogMode = DialogMode;

    constructor(
        private fb: FormBuilder,
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.createForm();
        this.recoverLastSearch();
    }

    private createForm(): void {
        this.form = this.fb.group({
            NumeroRegistro: '',
            NombreIndustria: '',
            IdProvincia: null,
            IdMunicipio: null,
            Activo: EnumSiNoTodos.SI,
            Autorizada: EnumSiNoTodos.TODOS,
        });

        if (this.mode === DialogMode.Dialog) {
            this.form.controls['Activo'].disable();
        }
    }

    /**
     * Recover last search from the previous payload
     */
    private recoverLastSearch(): void {
        if (this.lastFilter) {
            this.form.setValue({
                NumeroRegistro: this.lastFilter?.NumeroRegistro || '',
                NombreIndustria: this.lastFilter?.NombreIndustria || '',
                IdProvincia: this.lastFilter?.IdProvincia || null,
                IdMunicipio: this.lastFilter?.IdMunicipio || null,
                Activo: this.lastFilter?.Activo != null ? this.lastFilter?.Activo : EnumSiNoTodos.SI,
                Autorizada: this.lastFilter?.Autorizada != null ? this.lastFilter?.Autorizada : EnumSiNoTodos.TODOS,
            });
            this.form.markAsDirty();

            if (this.lastFilter.IdProvincia) {
                // Para recargar el desplegable de Municipios, vinculadas a la Provincia
                this.onSelectProvincia(null);
            }
            if (this.lastFilter.IdMunicipio) {
                this.setIdMunicipio(this.lastFilter.IdMunicipio);
            }

            // As soon as the user changes the "filter", the previous lastFilter pagination values has no sense
            this.form.valueChanges.pipe(debounceTime(500)).subscribe(values => {
                if (values) {
                    this.clearPaginationFilter();
                }
            });

            setTimeout(() => {
                this.onSearch(null);
            }, 200);
        }
    }

    /**
     * Manejador del evento para limpiar el formulario.
     * @param $event Evento javascript
     */
    resetForm($event: any): void {
        $event?.stopPropagation();
        this.form.setValue({
            NumeroRegistro: '',
            NombreIndustria: '',
            IdProvincia: null,
            IdMunicipio: null,
            Activo: EnumSiNoTodos.SI,
            Autorizada: EnumSiNoTodos.TODOS,
        });
        this.form.markAsPristine();

        this.clearPaginationFilter();

        this.searchingButton = false;
        this.isCollapsed = false;

        this.cleanForm.emit();
    }

    /**
     * We don't clear the whole filter but only the fields related to the pagination.
     */
    private clearPaginationFilter() {
        if (!this.lastFilter) {
            return;
        }
        this.lastFilter.PageNumber = 0;
        this.lastFilter.Asc = 0;
        this.lastFilter.OrderBy = '';
    }

    /**
     * Manejador del evento de búsqueda
     * @param $event Evento Javascript
     */
    onSearch($event: any): void {
        $event?.stopPropagation();
        this.searchingButton = true;
        this.isCollapsed = true;

        const payload = {
            ...this.form.value,
            PageNumber: this.lastFilter?.PageNumber || 0,
            Asc: this.lastFilter?.Asc || 0,
            OrderBy: this.lastFilter?.OrderBy || 'NumeroRegistro',
        } as FilterSearchingEmpresaDeshidratadoraViewModel;

        this.search.emit(payload);
    }

    /**
     * Manejador del evento de selección de una provincia en el desplegable.
     * Ignoramos el valor que viene en el parámetro y lo tomamos del form.
     * @param $event Id y descripción de la provincia seleccionada
     */
    onSelectProvincia($event: any): void {
        const idProvincia = this.form.value.IdProvincia;
        this.provinciaSelected.emit(idProvincia);
        this.form.patchValue({
            IdMunicipio: null,
        });
    }

    setIdMunicipio(idMunicipio: number | null): void {
        this.form.patchValue({
            IdMunicipio: idMunicipio,
        });
    }

    /**
     * Para controlar la propiedad disabled del botón de reset.
     * @returns boolean
     */
    formIsUntouched(): boolean {
        return this.form.pristine || !this.form.dirty;
    }

    /**
     * Para controlar la propiedad disabled del botón de búsqueda.
     * Mientras no estén cargados lss provincias, no se habilita el botón.
     * @returns boolean
     */
    get disableSearchBtn(): boolean {
        return this.isCollapsed || !this.provincias || this.provincias?.length == 0;
    }
}
